import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DWELLERS } from '../../data/dwellers';
import { QUESTS } from '../../data/quests';
import type { SpriteKey } from '../../types';
import { ACCENT, AGENTS } from './landingData';
import DwellerSprite from './DwellerSprite';
import MiniBunkerSim from './MiniBunkerSim';

const SILK = "'Silkscreen', monospace";
const VT = "'VT323', monospace";

const SHELL_CSS = `
  .ib-box { border: 2px solid #24382c; background: #080d0a; box-shadow: 0 0 0 1px #0a120d, 0 30px 70px -30px rgba(0,0,0,0.9); }
  .ib-shell { display: grid; grid-template-columns: 208px 1fr 268px; gap: 0; }
  .ib-col { height: 520px; overflow: hidden; position: relative; }
  .ib-quests { border-right: 1px solid #1c2b22; }
  .ib-agent { border-left: 1px solid #1c2b22; }
  .ib-scene { background: #090f0c; }
  .ib-term { display: flex; align-items: center; gap: 10px; border-top: 1px solid #1c2b22; background: #070c09; padding: 13px 16px; }
  .ib-open { opacity: 0; transition: opacity 160ms ease-out; }
  .ib-col:hover .ib-open, .ib-col:focus-within .ib-open { opacity: 1; }
  /* The quest board goes first: it is the one column that only ever links out.
     The agent panel is the payoff of clicking a dweller, so it survives until
     there is genuinely no room — and the invitation to click leaves with it. */
  @media (max-width: 1020px) {
    .ib-shell { grid-template-columns: 1fr 250px; }
    .ib-quests { display: none; }
  }
  @media (max-width: 720px) {
    .ib-shell { grid-template-columns: 1fr; }
    .ib-agent, .ib-click-hint { display: none; }
    .ib-col { height: 430px; }
  }
  /* The hint costs more than it earns once it starts truncating the prompt. */
  @media (max-width: 560px) {
    .ib-term-hint { display: none; }
  }
`;

/** Era 0 quests, shown the way the real board shows them. */
const ERA0 = QUESTS.filter((q) => q.era === 0);
/** Era 1 titles, greyed under a locked header — the board a founder actually sees. */
const ERA1 = QUESTS.filter((q) => q.era === 1).slice(0, 5);

function agentMeta(id: SpriteKey) {
  const card = AGENTS.find((a) => a.id === id);
  const dweller = DWELLERS.find((d) => d.id === id);
  return { card, dweller };
}

/**
 * The hero's centrepiece: a working miniature of the app shell — quest board,
 * live bunker, agent panel, terminal.
 *
 * Exactly one interaction resolves inside the page: clicking a dweller opens
 * that agent's real card in the right-hand panel. Everything else is a door to
 * /app. That split is deliberate — a demo where every click navigates away is
 * a conversion trap wearing a product's clothes, and a demo where nothing
 * happens is a screenshot.
 */
export default function InteractiveBunker() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<SpriteKey>('overseer');
  const open = useCallback(() => navigate('/app'), [navigate]);

  const { card, dweller } = useMemo(() => agentMeta(selected), [selected]);
  const idx = AGENTS.findIndex((a) => a.id === selected);
  const cycle = (step: number) => setSelected(AGENTS[(idx + step + AGENTS.length) % AGENTS.length].id);

  return (
    <div className="ib-box">
      <style>{SHELL_CSS}</style>
      <div className="ib-shell">
        {/* QUEST BOARD */}
        <div
          className="ib-col ib-quests"
          role="button"
          tabIndex={0}
          onClick={open}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
          aria-label="Open the quest board in the bunker"
          style={{ cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, borderBottom: '1px solid #1c2b22', padding: '10px 12px' }}>
            <span style={{ fontFamily: SILK, fontSize: 8, letterSpacing: 1, color: '#8fa196' }}>QUEST BOARD</span>
            <span className="ib-open" style={{ fontFamily: SILK, fontSize: 7, letterSpacing: 0.5, color: ACCENT }}>OPEN ▸</span>
          </div>
          <div style={{ padding: '10px 12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: SILK, fontSize: 8, letterSpacing: 0.5, color: ACCENT, marginBottom: 10 }}>
              <span>ERA 0 — IDENTITY</span><span>0/{ERA0.length}</span>
            </div>
            {ERA0.map((q) => (
              <div key={q.id} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', marginBottom: 10 }}>
                <span style={{ flexShrink: 0, width: 9, height: 9, marginTop: 3, border: `1px solid ${ACCENT}` }} />
                <span style={{ fontFamily: VT, fontSize: 15, lineHeight: 1.16, color: '#c2d2c6' }}>
                  {q.title}
                  <span style={{ display: 'block', fontFamily: SILK, fontSize: 6, letterSpacing: 0.5, color: '#5d7a66', marginTop: 3 }}>+{q.xp} XP</span>
                </span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: SILK, fontSize: 8, letterSpacing: 0.5, color: '#4f6357', margin: '16px 0 10px' }}>
              <span>ERA 1 — ATLAS</span><span aria-hidden="true">🔒</span>
            </div>
            {ERA1.map((q) => (
              <div key={q.id} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', marginBottom: 9 }}>
                <span style={{ flexShrink: 0, width: 9, height: 9, marginTop: 3, border: '1px solid #2a3a2f' }} />
                <span style={{ fontFamily: VT, fontSize: 15, lineHeight: 1.16, color: '#586b5f' }}>{q.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* BUNKER */}
        <div className="ib-col ib-scene">
          <MiniBunkerSim selectedId={selected} onSelect={setSelected} />
          <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', alignItems: 'center', gap: 7, fontFamily: SILK, fontSize: 8, letterSpacing: 1, color: '#e8ddc7', background: 'rgba(6,10,8,0.82)', border: '1px solid #2a3a2f', padding: '5px 9px' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#e05050', boxShadow: '0 0 8px 1px rgba(224,80,80,0.8)', animation: 'blink 1.4s step-end infinite' }} />
            LIVE — BUNKER 01
          </div>
          <div className="ib-click-hint" style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontFamily: SILK, fontSize: 8, letterSpacing: 0.5, color: ACCENT, background: 'rgba(6,10,8,0.82)', border: `1px solid ${ACCENT}`, padding: '5px 10px' }}>
            ▸ CLICK AN AGENT
          </div>
        </div>

        {/* AGENT PANEL */}
        <div className="ib-col ib-agent">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, borderBottom: '1px solid #1c2b22', padding: '10px 12px' }}>
            <span style={{ fontFamily: SILK, fontSize: 8, letterSpacing: 1, color: '#8fa196' }}>AGENT</span>
            <span style={{ display: 'flex', gap: 4 }}>
              <button type="button" onClick={() => cycle(-1)} aria-label="Previous agent" style={navBtn}>◂</button>
              <button type="button" onClick={() => cycle(1)} aria-label="Next agent" style={navBtn}>▸</button>
            </span>
          </div>
          <div style={{ padding: '14px 14px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <DwellerSprite id={selected} scale={2} />
              <span>
                <span style={{ display: 'block', fontFamily: VT, fontSize: 21, lineHeight: 1, color: '#e8ddc7' }}>{card?.name}</span>
                <span style={{ display: 'block', fontFamily: SILK, fontSize: 7, letterSpacing: 0.5, color: card?.color, marginTop: 5 }}>{card?.role}</span>
              </span>
            </div>
            <p style={{ fontFamily: VT, fontSize: 16, lineHeight: 1.22, color: '#9fb0a4', fontStyle: 'italic', margin: '0 0 6px' }}>&ldquo;{dweller?.tagline}&rdquo;</p>
            <div style={{ fontFamily: SILK, fontSize: 6, letterSpacing: 0.5, color: '#5d7a66', marginBottom: 14 }}>{dweller?.room.toUpperCase()}</div>

            <div style={{ fontFamily: SILK, fontSize: 8, letterSpacing: 1, color: '#8fa196', marginBottom: 9 }}>WHEN TO CALL</div>
            {dweller?.whenToCall.map((line) => (
              <div key={line} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', marginBottom: 7 }}>
                <span style={{ color: ACCENT, fontFamily: VT, fontSize: 15, lineHeight: 1.2 }}>&rsaquo;</span>
                <span style={{ fontFamily: VT, fontSize: 15, lineHeight: 1.2, color: '#9fb0a4' }}>{line}</span>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={open}
            style={{ position: 'absolute', left: 14, right: 14, bottom: 14, fontFamily: SILK, fontSize: 8, letterSpacing: 1, color: '#06110a', background: ACCENT, border: `1px solid ${ACCENT}`, padding: '10px 0', cursor: 'pointer' }}
          >
            TALK TO {card?.name.replace('The ', '').toUpperCase()} ▸
          </button>
        </div>
      </div>

      {/* TERMINAL — a real input, but the first keystroke belongs to the app. */}
      <div className="ib-term">
        <span style={{ fontFamily: VT, fontSize: 18, color: ACCENT }}>&gt;</span>
        <input
          type="text"
          readOnly
          onFocus={open}
          onClick={open}
          placeholder={`Ask ${card?.name ?? 'the Overseer'} anything…`}
          aria-label="Open the bunker terminal"
          style={{ flex: 1, minWidth: 0, fontFamily: VT, fontSize: 18, color: '#c2d2c6', background: 'transparent', border: 'none', outline: 'none', cursor: 'pointer' }}
        />
        <span className="ib-term-hint" style={{ fontFamily: SILK, fontSize: 7, letterSpacing: 0.5, color: '#5d7a66', whiteSpace: 'nowrap' }}>OPENS THE BUNKER ▸</span>
      </div>
    </div>
  );
}

const navBtn: React.CSSProperties = {
  fontFamily: VT, fontSize: 15, lineHeight: 1, color: '#9fb0a4',
  background: 'transparent', border: '1px solid #2a3a2f', padding: '3px 7px', cursor: 'pointer',
};
